import {
  Box,
  Button,
  canOpenCamera,
  captureCameraImage,
  Icon,
  type Omittable,
  pickImageFile,
  pickImageFiles,
  Text,
  toastManager,
  useIsMobile,
  useIsTouchDevice,
} from "@salestrak/ui";
import { useState } from "react";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { CameraCaptureDialog } from "#app/components/media/camera-capture-dialog";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import {
  DeletedMediaTile,
  OverflowMediaTile,
  RemovableMediaTile,
  ResolvedMediaTile,
  UploadingMediaTile,
} from "#app/components/media/media-tiles";
import { isMultipleField } from "#app/lib/custom-field-utils";
import type { PendingMediaUpload } from "#app/lib/hooks/use-pending-media-uploads";
import { stringIds } from "#app/lib/forms";
import { showUndoToast } from "#app/lib/undo-shortcut";
import { countOf, pluralize } from "#app/lib/utils/pluralize";

import { useFormPhotoUploadsContext } from "./form-photo-uploads";

const ROW_SLOTS_PHONE = 3;
const ROW_SLOTS = 4;


/** One row that never wraps, so a wall of photos can't push later questions down. */
export function FormPhotoGrid({
  ids,
  deleted,
  title,
  pending = [],
  onRemove,
  onDelete,
  onCancel,
}: Omittable<{
  ids: readonly string[];
  deleted?: readonly string[] | undefined;
  title: string;
  pending?: readonly PendingMediaUpload[];
  onRemove?: (photoId: string, index: number) => void;
  onDelete?: (photoId: string) => void;
  onCancel?: (tempId: string) => void;
}>) {
  const [viewing, setViewing] = useState<number | null>(null);
  const slots = useIsMobile() ? ROW_SLOTS_PHONE : ROW_SLOTS;
  const live = deleted ? ids.filter((id) => !deleted.includes(id)) : ids;
  const total = ids.length + pending.length;
  if (total === 0) return null;

  const overflowAt = total > slots ? slots - 1 : null;
  const shownIds = overflowAt === null ? ids : ids.slice(0, overflowAt);
  const shownPending =
    overflowAt === null ? pending : pending.slice(0, Math.max(overflowAt - ids.length, 0));
  const nextPhoto = overflowAt === null ? undefined : live.find((id) => id === ids[overflowAt]);
  // Overflow opens on the first photo past the row, or the last before it when only boxes follow.
  const overflowOpensAt =
    overflowAt === null ? -1 : Math.min(ids.slice(0, overflowAt).filter((id) => live.includes(id)).length, live.length - 1);
  const nextUpload =
    overflowAt === null || nextPhoto ? undefined : pending[overflowAt - ids.length];

  return (
    <>
      <Box columns={slots} fill="width" gap="sm">
        {shownIds.map((photoId, index) => {
          if (deleted?.includes(photoId)) return <DeletedMediaTile key={photoId} size="fill" />;
          const at = live.indexOf(photoId);
          const open = () => setViewing(at);
          const openLabel = `View photo ${at + 1} of ${live.length}`;
          return onRemove ? (
            <RemovableMediaTile
              key={photoId}
              onOpen={open}
              onRemove={() => onRemove(photoId, index)}
              openLabel={openLabel}
              photoId={photoId}
              size="fill"
            />
          ) : (
            <ResolvedMediaTile
              key={photoId}
              label={openLabel}
              onClick={open}
              photoId={photoId}
              size="fill"
            />
          );
        })}
        {shownPending.map((upload) => (
          <UploadingMediaTile
            key={upload.tempId}
            onCancel={onCancel ? () => onCancel(upload.tempId) : undefined}
            progress={upload.progress}
            size="fill"
            uri={upload.uri}
          />
        ))}
        {overflowAt !== null ? (
          <OverflowMediaTile
            count={total - overflowAt}
            // The viewer holds landed photos only.
            onClick={overflowOpensAt >= 0 ? () => setViewing(overflowOpensAt) : undefined}
            photoId={nextPhoto}
            size="fill"
            uri={nextUpload?.uri}
          />
        ) : null}
      </Box>
      <MediaLightbox
        initialIndex={viewing ?? 0}
        media={live.map((id) => ({ contentType: "image/*", id }))}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        onRemove={onRemove ?? onDelete}
        open={viewing !== null}
        title={title}
      />
    </>
  );
}

function carriesFiles(event: React.DragEvent): boolean {
  return event.dataTransfer.types.includes("Files");
}

function answerStatus({
  count,
  uploading,
  min,
  cap,
}: {
  count: number;
  uploading: number;
  min: number | null;
  cap: number | null;
}): string | null {
  if (uploading > 0) return `Uploading ${countOf(uploading, "photo")}…`;
  if (cap === 1) return null;
  if (min !== null && count < min) {
    return count === 0 ? `At least ${countOf(min, "photo")}` : `${min - count} more needed`;
  }
  return cap !== null ? `${count} of ${cap}` : null;
}

/** Uploads belong to the filling (`FormPhotoUploads`), so a photo still lands after the card leaves mid-upload. */
export function FormPhotosAnswer({
  itemId,
  field,
  label,
  value,
}: {
  itemId: string;
  field: CustomFieldDef;
  label: string;
  value: unknown;
}) {
  const uploads = useFormPhotoUploadsContext();
  const touch = useIsTouchDevice();
  const [shooting, setShooting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const ids = stringIds(value);
  const pending = uploads?.pending.filter((upload) => upload.key === itemId) ?? [];
  const multiple = isMultipleField(field.config);
  const min = typeof field.config?.min === "number" ? field.config.min : null;
  const max = typeof field.config?.max === "number" ? field.config.max : null;
  const cap = multiple ? max : 1;
  // A single photo is replaced, so it never runs out of room.
  const room = !multiple || cap === null ? null : Math.max(cap - ids.length - pending.length, 0);
  const full = room === 0;
  const replacing = !multiple && (ids.length > 0 || pending.length > 0);

  const addFrom = async (pick: () => Promise<File[]>) => {
    if (!uploads) return;
    const picked = await pick();
    const files = room === null ? picked.slice(0, multiple ? undefined : 1) : picked.slice(0, room);
    if (files.length < picked.length && multiple && cap !== null) {
      toastManager.add({
        description: `This question takes up to ${countOf(cap, "photo")}.`,
        title: `Added ${files.length} of ${countOf(picked.length, "photo")}`,
        type: "warning",
      });
    }
    if (files.length > 0) uploads.add(itemId, files, !multiple);
  };

  const remove = (photoId: string, index: number) => {
    if (!uploads) return;
    uploads.updateAnswer(itemId, (previous) =>
      stringIds(previous).filter((id) => id !== photoId),
    );
    showUndoToast({
      onUndo: () =>
        uploads.updateAnswer(itemId, (previous) => {
          const current = stringIds(previous);
          if (current.includes(photoId) || (!multiple && current.length > 0)) return current;
          const next = [...current];
          next.splice(Math.min(index, next.length), 0, photoId);
          return next;
        }),
      title: "Photo removed",
    });
  };

  const pickFromLibrary = () =>
    void addFrom(
      multiple ? pickImageFiles : () => pickImageFile().then((file) => (file ? [file] : [])),
    );

  const keepShots = (files: File[]) => {
    setShooting(false);
    if (files.length > 0) void addFrom(() => Promise.resolve(files));
  };

  const status = answerStatus({ cap, count: ids.length, min, uploading: pending.length });
  const pickLabel = touch
    ? "Photo library"
    : multiple
      ? "Add photos"
      : replacing
        ? "Replace photo"
        : "Add photo";

  // Desktop drop target; the canvas preview draws it but takes no drop.
  const dropArea = !touch && !full;
  const droppable = dropArea && uploads !== null;
  const empty = ids.length === 0 && pending.length === 0;
  const dropProps = droppable
    ? {
        onDragEnter: (event: React.DragEvent) => {
          if (carriesFiles(event)) setDragging(true);
        },
        // Leaving for a child is not leaving; browsers with no related target clear here and the next dragover sets it again.
        onDragLeave: (event: React.DragEvent) => {
          const to = event.relatedTarget;
          if (!(to instanceof Node && event.currentTarget.contains(to))) setDragging(false);
        },
        onDragOver: (event: React.DragEvent) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
          setDragging(true);
        },
        onDrop: (event: React.DragEvent) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          setDragging(false);
          const dropped = Array.from(event.dataTransfer.files);
          const images = dropped.filter((file) => file.type.startsWith("image/"));
          if (images.length < dropped.length) {
            toastManager.add({
              description: "Only photos can be added here.",
              title:
                images.length === 0
                  ? "No photos added"
                  : `Added ${images.length} of ${dropped.length} ${pluralize("file", { count: dropped.length })}`,
              type: "warning",
            });
          }
          if (images.length > 0) void addFrom(() => Promise.resolve(images));
        },
      }
    : {};

  const controls = (
    <>
      {touch || import.meta.env.DEV ? (
        <Button
          disabled={full}
          onClick={() =>
            canOpenCamera() ? setShooting(true) : void addFrom(captureCameraImage)
          }
          variant="field"
        >
          <Icon name="camera" size="md" />
          {replacing ? "Retake photo" : "Take photo"}
        </Button>
      ) : null}
      <Button disabled={full} onClick={pickFromLibrary} variant="field">
        <Icon name="image" size="md" />
        {pickLabel}
      </Button>
    </>
  );

  return (
    <Box direction="column" fill="width" gap="base">
      {dropArea && empty ? (
        <Box
          {...dropProps}
          align="center"
          background={dragging ? "selected" : undefined}
          border
          borderColor={dragging ? "primary" : "input"}
          borderStyle="dashed"
          className={dragging ? undefined : "hover:border-muted-foreground/60"}
          cursor="pointer"
          direction="column"
          fill="width"
          hoverBackground={!dragging}
          justify="center"
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            if (!(event.target instanceof Element && event.target.closest("button"))) {
              pickFromLibrary();
            }
          }}
          paddingX="lg"
          paddingY="md"
          position="relative"
          rounded="lg"
          transition="colors"
        >
          {/* The area keeps its size, so nothing under the pointer moves. */}
          <Box
            align="center"
            direction="column"
            gap="base"
            opacity={dragging ? 0 : 100}
            transition="opacity"
          >
            <Text size="sm" variant="muted">
              {multiple ? "Drag photos here" : "Drag a photo here"}
            </Text>
            <Box align="center" direction="row" gap="sm" justify="center" wrap>
              {controls}
            </Box>
            {status ? (
              <Text size="xs" variant="muted">
                {status}
              </Text>
            ) : null}
          </Box>
          <Box
            align="center"
            inset={0}
            justify="center"
            opacity={dragging ? 100 : 0}
            pointerEvents="none"
            position="absolute"
            transition="opacity"
          >
            <Text size="sm">{multiple ? "Drop photos to add" : "Drop a photo to add"}</Text>
          </Box>
        </Box>
      ) : (
        <Box
          {...dropProps}
          className={
            dragging
              ? "rounded-lg outline-1 outline-offset-4 outline-primary outline-dashed"
              : undefined
          }
          direction="column"
          fill="width"
          gap="base"
        >
          <FormPhotoGrid
            ids={ids}
            onCancel={uploads?.cancel}
            onRemove={remove}
            pending={pending}
            title={label}
          />
          <Box align="center" direction="row" fill="width" gap="sm" wrap>
            {controls}
            <Box grow />
            {status ? (
              <Text size="xs" variant="muted">
                {status}
              </Text>
            ) : null}
          </Box>
        </Box>
      )}
      <CameraCaptureDialog
        cap={cap}
        held={ids.length + pending.length}
        min={min}
        onDone={keepShots}
        onUseLibrary={pickFromLibrary}
        open={shooting}
        single={!multiple}
        title={label}
      />
    </Box>
  );
}
